import { ThemeProvider } from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import { useCurrentTenant } from "@qpr/client-library";
import axios from "axios";
import React from "react";
import { useQuery } from "react-query";
import { normalizeBundledAssetUrl } from "qpr-base/utils/url-utils";
import defaultTheme from "./themes/standard-solution-theme.json";
import backgroundImage from "../assets/background.png";
import adminToolsIcon from "../assets/navitems/AdminTools.svg";
import cascadeIcon from "../assets/navitems/cascade.svg";
import corporateIcon from "../assets/navitems/corporate.svg";
import entityIcon from "../assets/navitems/Entity.svg";
import executiveIcon from "../assets/navitems/executive.svg";
import iconMyDashboards from "../assets/navitems/icon_my_dashboards.svg";
import inputValueIcon from "../assets/navitems/InputValue.svg";
import kpisAndProgramsManagementIcon from "../assets/navitems/KPIsAndProgramsManagement.svg";
import kpisAndProjectsManagementIcon from "../assets/navitems/KPIsAndProjectsManagement.svg";
import manageWorkflowsIcon from "../assets/navitems/ManageWorkflows.svg";
import projectsIcon from "../assets/navitems/Projects.svg";
import reportingIcon from "../assets/navitems/reporting.svg";
import sectorsIcon from "../assets/navitems/Sectors.svg";
import strategicKpiIcon from "../assets/navitems/StrategicKpi.svg";
import workflowsIcon from "../assets/navitems/workflows.svg";

const NAVITEM_ICON_MAP: Record<string, string> = {
  "AdminTools.svg": adminToolsIcon,
  "cascade.svg": cascadeIcon,
  "corporate.svg": corporateIcon,
  "Entity.svg": entityIcon,
  "executive.svg": executiveIcon,
  "icon_my_dashboards.svg": iconMyDashboards,
  "InputValue.svg": inputValueIcon,
  "KPIsAndProgramsManagement.svg": kpisAndProgramsManagementIcon,
  "KPIsAndProjectsManagement.svg": kpisAndProjectsManagementIcon,
  "ManageWorkflows.svg": manageWorkflowsIcon,
  "Projects.svg": projectsIcon,
  "reporting.svg": reportingIcon,
  "Sectors.svg": sectorsIcon,
  "StrategicKpi.svg": strategicKpiIcon,
  "workflows.svg": workflowsIcon,
};

const BUNDLED_BACKGROUND_IMAGE = normalizeBundledAssetUrl(backgroundImage);
const BUNDLED_NAVITEM_ICON_MAP = Object.fromEntries(
  Object.entries(NAVITEM_ICON_MAP).map(([key, value]) => [
    key,
    normalizeBundledAssetUrl(value),
  ])
);

// Matches the entire URL up to a quote/paren/space boundary, so it also
// absorbs any tenant prefix that `setServerUrls` may have prepended
// (e.g. `/tdf/api/files/?file=/public/background.png`). Without this the
// replacement leaves the `/tdf/` prefix intact and produces
// `/tdf/static/media/background.HASH.png`, which the static handler can't
// serve and falls through to index.html — so the browser caches an HTML
// response for an image URL and never recovers (304 with mimeType text/html).
const rewriteThemeString = (value: string): string => {
  let result = value;
  result = result.replace(
    /[^"')\s]*\/(?:api\/)?files\/\?file=\/public\/background\.png(?:&[^"')\s]*)?/g,
    BUNDLED_BACKGROUND_IMAGE
  );
  result = result.replace(
    /[^"')\s]*\/(?:api\/)?files\/\?file=\/icons\/navitems\/([^"&')\s]+)(?:&[^"')\s]*)?/g,
    (fullMatch, iconName: string) =>
      BUNDLED_NAVITEM_ICON_MAP[iconName] || fullMatch
  );
  result = normalizeBundledAssetUrl(result);
  return result;
};

const rewriteThemeObject = (input: any): any => {
  if (typeof input === "string") return rewriteThemeString(input);
  if (Array.isArray(input)) return input.map((item) => rewriteThemeObject(item));
  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, rewriteThemeObject(value)])
    );
  }
  return input;
};

const Theme = (props: React.PropsWithChildren<{ obj?: any }>) => {
  const tenant = useCurrentTenant();
  const { data, isLoading } = useQuery(
    [`theme-${tenant?.symbol}`],
    () => {
      return axios.get("/themes", {
        params: {
          symbol: tenant?.symbol || "",
        },
      });
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  let theme;

  // Comment out following line to use defaulTheme (standard-solution-theme.json file) instead of theme saved to mongo
  // This helps in creating & modifying theme settings as changes to css are visible immediately
  if (!isLoading && data) theme = data?.data.data[0]?.themeOptions;

  const setServerUrls = (themeData: any) => {
    let themeStr = JSON.stringify(themeData);
    themeStr = themeStr
      .split("url(")
      .join(`url(${process.env.REACT_APP_SERVER_URL}`);
    return rewriteThemeObject(JSON.parse(themeStr));
  };

  const useDefaultTheme =
    !theme || typeof theme !== "object" || Object.keys(theme).length === 0;

  let themeObj =
    props.obj || setServerUrls(useDefaultTheme ? defaultTheme : theme);

  themeObj = createTheme(themeObj);

  return <ThemeProvider theme={themeObj}>{props.children}</ThemeProvider>;
};

export default Theme;
